import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Zap,
  Settings,
  Eye,
  AlertTriangle,
  Info,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useMultipleIntegrationStatus } from "@/hooks/useIntegrationStatus";
import { integrationHealthService } from "@/services/integration-health.service";
import type {
  IntegrationType,
  ConnectionHealth,
  ConnectionStatus,
} from "@/services/integration-health.service";

interface IntegrationHealthSummary {
  type: IntegrationType;
  name: string;
  status: "connected" | "disconnected" | "error" | "degraded" | "pending";
  health?: ConnectionHealth;
  lastSync?: string;
  dataQuality: number;
  criticalIssues: number;
  warningIssues: number;
  uptime: number;
}

interface OverallHealth {
  score: number;
  status: "excellent" | "good" | "warning" | "critical";
  connectedIntegrations: number;
  totalIntegrations: number;
  criticalIssues: number;
  totalIssues: number;
}

const integrationDefinitions: Array<{ type: IntegrationType; name: string }> = [
  { type: "google_ads", name: "Google Ads" },
  { type: "google_analytics", name: "Google Analytics" },
  { type: "meta_ads", name: "Meta Ads" },
  { type: "search_console", name: "Search Console" },
  { type: "instagram_insights", name: "Instagram Insights" },
];

const integrationStatusRequests = integrationDefinitions.map(({ type }) => ({
  type,
  id: `${type}-main`,
}));

const HealthDashboard: React.FC = () => {
  const integrationStatuses = useMultipleIntegrationStatus(
    integrationStatusRequests,
  );

  const [healthSummaries, setHealthSummaries] = useState<
    IntegrationHealthSummary[]
  >([]);
  const [overallHealth, setOverallHealth] = useState<OverallHealth>({
    score: 0,
    status: "critical",
    connectedIntegrations: 0,
    totalIntegrations: 5,
    criticalIssues: 0,
    totalIssues: 0,
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>("");

  // Carregar dados de saúde
  const loadHealthData = async () => {
    try {
      setLoading(true);

      const summaries: IntegrationHealthSummary[] = [];
      let totalScore = 0;
      let connectedCount = 0;
      let totalCriticalIssues = 0;
      let totalAllIssues = 0;

      for (const integration of integrationDefinitions) {
        const statusData = integrationStatuses.statuses.find(
          (s) => s.integrationType === integration.type,
        );
        const health = statusData?.health;
        const status: ConnectionStatus =
          statusData?.status ?? health?.status ?? "disconnected";

        const criticalIssues =
          health?.issues.filter((i) => i.severity === "critical").length || 0;
        const warningIssues =
          health?.issues.filter((i) => i.severity === "warning").length || 0;

        const summary: IntegrationHealthSummary = {
          type: integration.type,
          name: integration.name,
          status,
          health: health ?? undefined,
          lastSync: health?.lastSync,
          dataQuality: health?.dataQuality || 0,
          criticalIssues,
          warningIssues,
          uptime: health?.uptime || 0,
        };

        summaries.push(summary);

        if (status === "connected") {
          connectedCount++;
          totalScore += health?.score || 0;
        }

        totalCriticalIssues += criticalIssues;
        totalAllIssues += criticalIssues + warningIssues;
      }

      const avgScore = connectedCount > 0 ? totalScore / connectedCount : 0;
      const overallStatus: OverallHealth["status"] =
        avgScore >= 80
          ? "excellent"
          : avgScore >= 60
            ? "good"
            : avgScore >= 40
              ? "warning"
              : "critical";

      setHealthSummaries(summaries);
      setOverallHealth({
        score: avgScore,
        status: overallStatus,
        connectedIntegrations: connectedCount,
        totalIntegrations: integrationDefinitions.length,
        criticalIssues: totalCriticalIssues,
        totalIssues: totalAllIssues,
      });

      setLastUpdate(new Date().toLocaleString("pt-BR"));
    } catch (error) {
      console.error("Erro ao carregar dados de saúde:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadHealthData();
    const interval = setInterval(loadHealthData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationStatuses]);

  const getStatusIcon = (status: IntegrationHealthSummary["status"]) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "degraded":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case "pending":
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <WifiOff className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: IntegrationHealthSummary["status"]) => {
    switch (status) {
      case "connected":
        return "bg-green-100 text-green-800";
      case "error":
        return "bg-red-100 text-red-800";
      case "degraded":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getOverallStatusInfo = () => {
    const { status } = overallHealth;
    switch (status) {
      case "excellent":
        return {
          color: "bg-green-50 border-green-200",
          textColor: "text-green-900",
          icon: <CheckCircle className="h-6 w-6 text-green-600" />,
          title: "Excelente",
          message: "Todas as integrações estão funcionando perfeitamente",
        };
      case "good":
        return {
          color: "bg-blue-50 border-blue-200",
          textColor: "text-blue-900",
          icon: <Wifi className="h-6 w-6 text-blue-600" />,
          title: "Bom",
          message: "A maioria das integrações está funcionando bem",
        };
      case "warning":
        return {
          color: "bg-yellow-50 border-yellow-200",
          textColor: "text-yellow-900",
          icon: <AlertTriangle className="h-6 w-6 text-yellow-600" />,
          title: "Atenção",
          message: "Algumas integrações precisam de atenção",
        };
      case "critical":
        return {
          color: "bg-red-50 border-red-200",
          textColor: "text-red-900",
          icon: <AlertCircle className="h-6 w-6 text-red-600" />,
          title: "Crítico",
          message: "Problemas críticos detectados nas integrações",
        };
    }
  };

  const forceRefreshAll = async () => {
    setLoading(true);
    try {
      // Forçar verificação de saúde para todas as integrações
      for (const summary of healthSummaries) {
        if (summary.status === "connected") {
          await integrationHealthService.forceHealthCheck(
            summary.type,
            `${summary.type}-main`,
          );
        }
      }
      await loadHealthData();
    } catch (error) {
      console.error("Erro ao forçar atualização:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = getOverallStatusInfo();

  return (
    <div className="space-y-6">
      {/* Header com status geral */}
      <Card className={`${statusInfo.color} border-2`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {statusInfo.icon}
              <div>
                <h2 className={`text-2xl font-bold ${statusInfo.textColor}`}>
                  Saúde das Integrações: {statusInfo.title}
                </h2>
                <p className={`${statusInfo.textColor} opacity-80`}>
                  {statusInfo.message}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${statusInfo.textColor}`}>
                {overallHealth.score.toFixed(0)}/100
              </div>
              <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
                Score Geral
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`text-2xl font-bold ${statusInfo.textColor}`}>
                {overallHealth.connectedIntegrations}
              </div>
              <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
                Conectadas
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${statusInfo.textColor}`}>
                {overallHealth.totalIntegrations}
              </div>
              <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
                Total
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${statusInfo.textColor}`}>
                {overallHealth.criticalIssues}
              </div>
              <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
                Problemas Críticos
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${statusInfo.textColor}`}>
                {overallHealth.totalIssues}
              </div>
              <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
                Total de Problemas
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className={`text-sm ${statusInfo.textColor} opacity-80`}>
              Última atualização: {lastUpdate}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={forceRefreshAll}
              disabled={loading}
              className="border-current"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Atualizar Tudo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs para diferentes visualizações */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1">
          <TabsTrigger value="overview" className="text-xs sm:text-sm">
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="issues" className="text-xs sm:text-sm">
            Problemas
          </TabsTrigger>
          <TabsTrigger value="performance" className="text-xs sm:text-sm">
            Performance
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm">
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {healthSummaries.map((summary) => (
              <Card
                key={summary.type}
                className="hover:shadow-md transition-shadow"
              >
                <CardHeader className="">
                  <div className="flex items-center justify-between">
                    <CardTitle>{summary.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(summary.status)}
                      <Badge
                        variant="outline"
                        className={getStatusColor(summary.status)}
                      >
                        {summary.status === "connected"
                          ? "Conectado"
                          : summary.status === "error"
                            ? "Erro"
                            : summary.status === "degraded"
                              ? "Degradado"
                              : summary.status === "pending"
                                ? "Pendente"
                                : "Desconectado"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {summary.health && (
                    <>
                      {/* Score de saúde */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            Score de Saúde
                          </span>
                          <span
                            className={`text-sm font-bold ${getHealthColor(summary.health.score)}`}
                          >
                            {summary.health.score.toFixed(0)}/100
                          </span>
                        </div>
                        <Progress
                          value={summary.health.score}
                          className="h-2"
                        />
                      </div>

                      {/* Qualidade dos dados */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            Qualidade dos Dados
                          </span>
                          <span
                            className={`text-sm font-bold ${getHealthColor(summary.dataQuality)}`}
                          >
                            {summary.dataQuality.toFixed(0)}%
                          </span>
                        </div>
                        <Progress value={summary.dataQuality} className="h-2" />
                      </div>

                      {/* Uptime */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Uptime</span>
                          <span
                            className={`text-sm font-bold ${getHealthColor(summary.uptime * 100)}`}
                          >
                            {(summary.uptime * 100).toFixed(1)}%
                          </span>
                        </div>
                        <Progress
                          value={summary.uptime * 100}
                          className="h-2"
                        />
                      </div>
                    </>
                  )}

                  {/* Issues summary */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span>{summary.criticalIssues} crítico(s)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <span>{summary.warningIssues} alerta(s)</span>
                    </div>
                  </div>

                  {/* Última sincronização */}
                  {summary.lastSync && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>
                        Última sync:{" "}
                        {new Date(summary.lastSync).toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      <Eye className="h-3 w-3 mr-1" />
                      Detalhes
                    </Button>
                    <Button size="sm" variant="outline">
                      <Settings className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="issues" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Problemas Detectados</CardTitle>
              <CardDescription>
                Lista de todos os problemas identificados nas integrações
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {healthSummaries
                  .flatMap(
                    (summary) =>
                      summary.health?.issues.map((issue) => ({
                        ...issue,
                        integrationName: summary.name,
                        integrationType: summary.type,
                      })) || [],
                  )
                  .sort((a, b) => {
                    const severityOrder = { critical: 0, warning: 1, info: 2 };
                    return (
                      severityOrder[a.severity] - severityOrder[b.severity]
                    );
                  })
                  .map((issue, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg ${
                        issue.severity === "critical"
                          ? "border-red-200 bg-red-50"
                          : issue.severity === "warning"
                            ? "border-yellow-200 bg-yellow-50"
                            : "border-blue-200 bg-blue-50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {issue.severity === "critical" ? (
                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                          ) : issue.severity === "warning" ? (
                            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                          ) : (
                            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">
                                {issue.message}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {issue.integrationName}
                              </Badge>
                            </div>
                            {issue.description && (
                              <p className="text-sm text-muted-foreground mb-2">
                                {issue.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>
                                {new Date(issue.timestamp).toLocaleString(
                                  "pt-BR",
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {issue.action && (
                            <Button size="sm" variant="outline">
                              <Zap className="h-3 w-3 mr-1" />
                              {issue.action}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                {healthSummaries.every((s) => !s.health?.issues.length) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p>Nenhum problema detectado! 🎉</p>
                    <p className="text-sm">
                      Todas as integrações estão funcionando perfeitamente.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Performance metrics cards */}
            <Card>
              <CardHeader>
                <CardTitle>Métricas de Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {healthSummaries.map((summary) => (
                    <div
                      key={summary.type}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium">
                        {summary.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div
                            className={`text-sm font-bold ${getHealthColor(summary.health?.score || 0)}`}
                          >
                            {summary.health?.score.toFixed(0) || 0}/100
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(summary.uptime * 100).toFixed(1)}% uptime
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Qualidade dos Dados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {healthSummaries.map((summary) => (
                    <div key={summary.type}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">
                          {summary.name}
                        </span>
                        <span
                          className={`text-sm font-bold ${getHealthColor(summary.dataQuality)}`}
                        >
                          {summary.dataQuality.toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={summary.dataQuality} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Sincronizações</CardTitle>
              <CardDescription>
                Últimas sincronizações realizadas para cada integração
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {healthSummaries.map((summary) => (
                  <div
                    key={summary.type}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(summary.status)}
                      <div>
                        <div className="font-medium">{summary.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {summary.lastSync
                            ? `Última sync: ${new Date(summary.lastSync).toLocaleString("pt-BR")}`
                            : "Nunca sincronizado"}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Sincronizar
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HealthDashboard;
